/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { type AxiosError, type AxiosInstance } from 'axios';
import {
  type DealConstraintSkuDetailsData,
  type DealConstraintSkuDetailsResponse,
} from '../interface';
import { requestBaseURL } from '@/config/serviceAPI';
import { createQuery } from '@/utils/createReactQuery';
import { isAxiosError } from '@/utils/errorsHandling';
import { useQueryResultOnError } from '@/hooks/query/useQueryResultOnError';
import { type AcesDealsVehicleAttributes } from '@/features/deals';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useAcesDealsVehicleAttributes } from '@/features/deals/hooks/useAcesDealsVehicleAttributes';
type ApiVersion = 'v3' | 'v4';

type OptionsV3 = {
  apiVersion: 'v3';
  dealId: string;
  parentSku: string;
  constraintSkusList: string[];
  fulfillmentOption: string;
};

type OptionsV4 = {
  apiVersion: 'v4';
  dealId: string;
  parentSku: string;
  constraintSkusList: string[];
  fulfillmentOption: string;
  dealsVehicleAttributes: AcesDealsVehicleAttributes | null;
};

type Options = OptionsV3 | OptionsV4;
const urlByVersion: Record<ApiVersion, string> = {
  v3: `${requestBaseURL}/ecomm/b2c/browse/v3/skus`,
  v4: `${requestBaseURL}/ecomm/b2c/browse/v4/skus`,
};

export const getDealConstraintSkuDetails = async (
  options: Options,
  axiosInstance?: AxiosInstance,
) => {
  const { dealId, parentSku, constraintSkusList, fulfillmentOption, apiVersion } = options;

  if (constraintSkusList.length <= 0) {
    throw new Error('constraintSkuCsv is required');
  }
  const constraintSkuCsv = constraintSkusList.join(',');
  const baseUrl = urlByVersion[apiVersion];
  const url = `${baseUrl}/${parentSku}/deals/${dealId}/constraint-skus/${constraintSkuCsv}/fulfillment-option/${fulfillmentOption}`;
  const params =
    apiVersion === 'v4' && options.dealsVehicleAttributes ? options.dealsVehicleAttributes : null;

  const response = await getAxios(axiosInstance).get<DealConstraintSkuDetailsResponse>(url, {
    params,
  });

  return selector(response.data);
};

const selector = (data: DealConstraintSkuDetailsResponse): DealConstraintSkuDetailsData => data;

const { useData } = createQuery<DealConstraintSkuDetailsData, Options>(
  'dealConstraintSkuDetails',
  getDealConstraintSkuDetails,
);

type PublicOptions = Omit<Options, 'apiVersion' | 'dealsVehicleAttributes'> & {
  enabled?: boolean;
  onError?: (err: AxiosError) => void;
};

export const useDealConstraintSkuDetails = ({
  onError,
  constraintSkusList,
  dealId,
  parentSku,
  ...rest
}: PublicOptions) => {
  const { acesEnabled } = useAcesFlags();
  const dealsVehicleAttributes = useAcesDealsVehicleAttributes();
  const enabled = Boolean(constraintSkusList.length > 0 && dealId && parentSku);
  const queryResult = useData({
    apiVersion: acesEnabled ? 'v4' : 'v3',
    dealsVehicleAttributes,
    enabled,
    constraintSkusList,
    dealId,
    parentSku,
    ...rest,
  });

  useQueryResultOnError(queryResult, (err) => {
    if (isAxiosError(err)) {
      onError?.(err);
    }
  });

  return queryResult;
};
